import type { TFunction } from 'i18next';
import { Bell, Check, Receipt } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { formatDateTime } from '@/expenses/format-date';
import { monthLabel } from '@/expenses/list/list-query';
import type { ResolvedModule } from '../model/resolve-navigation';
import {
  type ExpenseNotification,
  notificationPath,
  useExpenseNotifications,
} from './expenses-notifications';

/** The kinds the bell has words for; any other (a newer server's) reads as a generic update. */
const KNOWN_KINDS: ReadonlySet<string> = new Set([
  'report_submitted',
  'report_awaiting_second_level',
  'report_returned',
  'report_accepted',
  'report_refused',
  'payment_recorded',
  'approval_waiting_reminder',
  'draft_reminder',
  'draft_refused_at_closing',
  'month_closing_reminder',
  'flat_rate_swept_off',
  'flat_rate_swept_on',
  'flat_rate_line_removed',
]);

function notificationLabel(
  t: TFunction<'shell'>,
  language: string,
  notification: ExpenseNotification,
): string {
  const values = {
    number: notification.reportNumber ?? t('bell.notifications.unnumbered'),
    month: notification.month === null ? '' : monthLabel(notification.month, language),
  };
  // The kind is read as the string the server sent, which may be one this build does not know.
  const kind: string = notification.kind;
  if (KNOWN_KINDS.has(kind)) {
    return t(`bell.notifications.kind.${kind as ExpenseNotification['kind']}`, values);
  }
  return notification.reportId === null
    ? t('bell.notifications.unknown')
    : t('bell.notifications.unknownForNote', values);
}

/**
 * The notifications bell (decision 54: «campanella ok»).
 *
 * WHAT IT LISTS, FOR NOW (decision 199, #872). Decision 54 keeps the bell and leaves its final
 * content open (#251), and an empty bell would be an inert control. For now it lists the attention
 * counters the rail already shows — today the at-risk visits of the clinical-study module — each
 * linking to its module, and apart from them the person's unread notifications (below). When a
 * module contributes a counter, it appears here too, with no change to this file. When there is
 * nothing, it says so rather than showing an empty box.
 *
 * THE SAME DISCLOSURE AS THE ACCOUNT MENU, for the same reasons (`rail/AccountMenu.tsx`): a button
 * with `aria-expanded` controlling a region, Escape closes and returns focus, a click outside
 * closes. Not a `menu`: these are links.
 *
 * AND THE PERSON'S UNREAD «NOTE SPESE» NOTIFICATIONS (#865, decision 197 A.3, #861), below the counters:
 * each links to its note, and opening it marks it read; a button beside it marks it read without
 * leaving the page. There is no «mark all read»: the API marks one at a time. A kind this build has
 * no words for reads as a generic update, never an error (`expenses-notifications.ts` reads them).
 *
 * THE DOT IS NOT THE ACCENT. It is `--attention`, amber: the action colour belongs to the one
 * primary action per view, never to navigation or counters (Brand Kit 1.0.0). With unread
 * notifications the dot becomes a badge with their number, in the same amber.
 */
export function AttentionBell({ modules }: { readonly modules: readonly ResolvedModule[] }) {
  const { t, i18n } = useTranslation('shell');
  const { t: tCommon } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const items = modules.filter((module) => module.count !== undefined);
  const notifications = useExpenseNotifications();
  // «Riprova» was pressed: when its line gives way, focus goes on rather than to the page.
  const retried = useRef(false);
  const notificationsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!retried.current || notifications.refreshing) return;
    retried.current = false;
    // Failed again: the line and its button stay, and so does the focus.
    if (notifications.status === 'unavailable') return;
    const firstRow = notificationsRef.current?.querySelector<HTMLElement>('.shell-bell-item');
    (firstRow ?? triggerRef.current)?.focus();
  }, [notifications.status, notifications.refreshing]);
  const unread = notifications.unread;
  // EVERYTHING THE PANEL LISTS, COUNTED ONCE: the modules with a counter and the unread
  // notifications together. A floor with «+» when one read could not count the notifications all
  // (50 or more, or 5 pages with more to read); never a number the bell has not established.
  const total = items.length + unread.length;
  // Not all counted: the notifications reached a bound, or they are not known yet (loading,
  // unavailable, or pages that ran out) while the modules already have something to count.
  const notificationsUnknown =
    notifications.status !== 'ready' || (notifications.truncated && unread.length === 0);
  const bounded =
    (unread.length > 0 && (notifications.capped || notifications.truncated)) ||
    (items.length > 0 && notificationsUnknown);
  const badge = bounded ? `${total}+` : String(total);
  // The name speaks the badge's number; with modules only, the dot's count.
  const label =
    total === 0
      ? t('bell.label')
      : bounded
        ? t('bell.labelAtLeast', { count: total })
        : t('bell.labelWithCount', { count: total });

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        containerRef.current !== null &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="shell-bell" ref={containerRef}>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="icon"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        onClick={() => {
          if (!open) notifications.reload();
          setOpen((current) => !current);
        }}
      >
        <Bell aria-hidden="true" />
        {unread.length > 0 || bounded ? (
          <span
            className="shell-bell-badge"
            data-wide={bounded ? 'true' : undefined}
            aria-hidden="true"
          >
            {badge}
          </span>
        ) : items.length > 0 ? (
          <span className="shell-bell-dot" aria-hidden="true" />
        ) : null}
      </Button>
      {open ? (
        <div className="shell-bell-panel" id={panelId}>
          <p className="shell-bell-title">{t('bell.title')}</p>
          {/* «Nothing» only once the notifications have answered, never while they are on their way
              and never when they could not be read. */}
          {total === 0 && notifications.status === 'ready' && !notifications.truncated ? (
            <p className="shell-bell-empty">{t('bell.empty')}</p>
          ) : null}
          {items.length === 0 ? null : (
            <ul className="shell-bell-list">
              {items.map((module) => (
                <li key={module.id}>
                  <Link className="shell-bell-item" to={module.path} onClick={() => setOpen(false)}>
                    <module.icon aria-hidden="true" />
                    <span className="shell-bell-module">{module.label}</span>
                    <span className="shell-bell-count">{module.count?.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {/* The notifications' own region: busy while a read is on its way (the first, or a
              re-read that keeps the list mounted), one calm line when it could not be read; the
              module counters above stay usable either way. */}
          <div
            ref={notificationsRef}
            className="shell-bell-notifications"
            aria-busy={notifications.status === 'loading' || notifications.refreshing}
          >
            {notifications.status === 'loading' ? (
              // The platform's loading state: a skeleton in the rows' own shape, announced once.
              <SkeletonRegion
                className="shell-bell-skeleton"
                label={t('bell.notifications.loading')}
              >
                {[0, 1].map((row) => (
                  <div key={row} className="shell-bell-skeleton-row">
                    <Skeleton className="shell-bell-skeleton-icon" />
                    <Skeleton className="shell-bell-skeleton-text" />
                    <Skeleton className="shell-bell-skeleton-time" />
                  </div>
                ))}
              </SkeletonRegion>
            ) : notifications.status === 'unavailable' ? (
              // The platform's error state: what went wrong, and a way to try again.
              <div className="shell-bell-error">
                <p className="shell-bell-status">{t('bell.notifications.unavailable')}</p>
                {/* `aria-disabled`, not `disabled`, while it reads: a disabled button drops the focus
                    it holds. The click is ignored meanwhile instead. `size="sm"` is 32px tall, and
                    a touch target is 44 at least (ui-design-guidelines § The bar): `min-h-11`, a
                    utility, because a `shell.css` rule loses to the button's own `h-8`. Dimmed while it reads. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-11 aria-disabled:cursor-progress aria-disabled:opacity-50"
                  aria-disabled={notifications.refreshing}
                  onClick={() => {
                    if (notifications.refreshing) return;
                    retried.current = true;
                    notifications.retry();
                  }}
                >
                  {tCommon('retry')}
                </Button>
              </div>
            ) : notifications.truncated && unread.length === 0 ? (
              // The pages ran out with none unread: not «nothing», for older ones may be unread.
              <p className="shell-bell-status">{t('bell.notifications.olderUnknown')}</p>
            ) : null}
            {unread.length === 0 ? null : (
              <>
                <p className="shell-bell-section">{t('bell.notifications.title')}</p>
                <ul className="shell-bell-list">
                  {unread.map((notification) => {
                    const label = notificationLabel(t, i18n.language, notification);
                    return (
                      <li key={notification.id} className="shell-bell-notification">
                        <Link
                          className="shell-bell-item"
                          to={notificationPath(notification)}
                          onClick={() => {
                            notifications.markRead(notification.id);
                            setOpen(false);
                          }}
                        >
                          <Receipt aria-hidden="true" />
                          <span className="shell-bell-text">{label}</span>
                          <span className="shell-bell-time">
                            {formatDateTime(notification.createdAt)}
                          </span>
                        </Link>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shell-bell-read"
                          aria-label={t('bell.notifications.markRead', { label })}
                          title={t('bell.notifications.markReadShort')}
                          onClick={(event) => {
                            // The row goes away: focus moves to the next one's button, else the bell.
                            const row = event.currentTarget.closest('li');
                            const next = (
                              row?.nextElementSibling ?? row?.previousElementSibling
                            )?.querySelector<HTMLButtonElement>('.shell-bell-read');
                            (next ?? triggerRef.current)?.focus();
                            notifications.markRead(notification.id);
                          }}
                        >
                          <Check aria-hidden="true" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
          {/* What the bell finally lists is still open (decision 54, #251); what it lists now is
              for now (decision 199, #872), and it says so. */}
          <p className="shell-bell-note">{t('bell.provisional')}</p>
        </div>
      ) : null}
    </div>
  );
}
